import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Loader2, RefreshCw } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";

const PLATFORM_COLORS = {
  tiktok: "#ec4899",
  instagram: "#a855f7",
  facebook: "#3b82f6",
  youtube: "#ef4444",
  other: "#6b7280",
};

const TrendsTab = ({ isDark, getTrends, filters }) => {
  const [data, setData] = useState({ weeks: [], byPlatform: [] });
  const [loading, setLoading] = useState(false);
  const [enabledPlatforms, setEnabledPlatforms] = useState({});

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrends(filters);
      setData(res || { weeks: [], byPlatform: [] });
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [getTrends, filters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Pivot byPlatform → wide format keyed on week, per platform metric.
  const platformList = useMemo(() => {
    return Array.from(new Set((data.byPlatform || []).map((r) => r.platform)));
  }, [data.byPlatform]);

  useEffect(() => {
    // Initialize toggle state on first load
    setEnabledPlatforms((prev) => {
      const next = { ...prev };
      platformList.forEach((p) => {
        if (!(p in next)) next[p] = true;
      });
      return next;
    });
  }, [platformList]);

  const chartData = useMemo(() => {
    const map = new Map();
    (data.weeks || []).forEach((w) => {
      map.set(w.week, {
        week: w.week,
        avgViews: w.avgViews,
        avgEngagement: Number((w.avgEngagement * 100).toFixed(2)),
        videoCount: w.videoCount,
      });
    });
    (data.byPlatform || []).forEach((r) => {
      const existing = map.get(r.week) || { week: r.week };
      existing[`views_${r.platform}`] = r.avgViews;
      existing[`eng_${r.platform}`] = Number((r.avgEngagement * 100).toFixed(2));
      map.set(r.week, existing);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.week.localeCompare(b.week)
    );
  }, [data]);

  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";

  const togglePlatform = (p) =>
    setEnabledPlatforms((prev) => ({ ...prev, [p]: !prev[p] }));

  return (
    <div
      className={cn(
        "rounded-xl border",
        isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <TrendingUp className={cn("h-4 w-4", isDark ? "text-blue-400" : "text-blue-500")} />
          <h2 className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>
            Trends (laatste 12 weken)
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refetch}
          className="h-7 gap-1 text-[12px]"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Vernieuwen
        </Button>
      </div>

      {platformList.length > 0 && (
        <div
          className={cn(
            "flex flex-wrap items-center gap-1.5 px-4 py-2 border-b",
            isDark ? "border-white/[0.06]" : "border-gray-100"
          )}
        >
          <span className={cn("text-[10px] uppercase tracking-wider font-semibold mr-1", isDark ? "text-gray-500" : "text-gray-400")}>
            Series
          </span>
          {platformList.map((p) => {
            const active = enabledPlatforms[p];
            return (
              <button
                key={p}
                onClick={() => togglePlatform(p)}
                className={cn(
                  "px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors capitalize flex items-center gap-1.5",
                  active
                    ? isDark
                      ? "bg-white/[0.08] text-white border-white/[0.12]"
                      : "bg-gray-900 text-white border-gray-900"
                    : isDark
                      ? "bg-transparent text-gray-500 border-white/[0.08]"
                      : "bg-transparent text-gray-500 border-gray-200"
                )}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: PLATFORM_COLORS[p] || PLATFORM_COLORS.other }}
                />
                {p}
              </button>
            );
          })}
        </div>
      )}

      <div className="p-3">
        {loading && chartData.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className={cn("h-6 w-6 animate-spin", isDark ? "text-gray-500" : "text-gray-400")} />
          </div>
        ) : chartData.length === 0 ? (
          <div className={cn("text-center py-16 text-[13px]", isDark ? "text-gray-500" : "text-gray-500")}>
            Nog geen gepubliceerde video's met data — voeg video's toe met een publicatiedatum.
          </div>
        ) : (
          <div className="space-y-6">
            <ChartBlock title="Gemiddelde views per week" isDark={isDark}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="week" stroke={axisColor} tick={{ fontSize: 11 }} />
                  <YAxis stroke={axisColor} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? "rgba(20,20,25,0.95)" : "rgba(255,255,255,0.95)",
                      border: `1px solid ${gridColor}`,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="avgViews"
                    name="Totaal"
                    stroke={isDark ? "#60a5fa" : "#2563eb"}
                    strokeWidth={2}
                    dot={false}
                  />
                  {platformList
                    .filter((p) => enabledPlatforms[p])
                    .map((p) => (
                      <Line
                        key={`views_${p}`}
                        type="monotone"
                        dataKey={`views_${p}`}
                        name={p}
                        stroke={PLATFORM_COLORS[p] || PLATFORM_COLORS.other}
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartBlock>

            <ChartBlock title="Gemiddelde engagement rate (%)" isDark={isDark}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="week" stroke={axisColor} tick={{ fontSize: 11 }} />
                  <YAxis stroke={axisColor} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? "rgba(20,20,25,0.95)" : "rgba(255,255,255,0.95)",
                      border: `1px solid ${gridColor}`,
                      fontSize: 12,
                    }}
                    formatter={(v) => `${v}%`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="avgEngagement"
                    name="Totaal"
                    stroke={isDark ? "#34d399" : "#059669"}
                    strokeWidth={2}
                    dot={false}
                  />
                  {platformList
                    .filter((p) => enabledPlatforms[p])
                    .map((p) => (
                      <Line
                        key={`eng_${p}`}
                        type="monotone"
                        dataKey={`eng_${p}`}
                        name={p}
                        stroke={PLATFORM_COLORS[p] || PLATFORM_COLORS.other}
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartBlock>
          </div>
        )}
      </div>
    </div>
  );
};

const ChartBlock = ({ title, isDark, children }) => (
  <div>
    <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-400" : "text-gray-500")}>
      {title}
    </p>
    {children}
  </div>
);

export default TrendsTab;
